import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Divider,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  Google as GoogleIcon,
  Key as KeyIcon,
  Phone as PhoneIcon,
  Lock as LockIcon,
  Fingerprint as FingerprintIcon,
  QrCode2 as QrCodeIcon,
  Facebook as FacebookIcon,
} from '@mui/icons-material';

interface AuthState {
  email: string;
  phoneNumber: string;
  verificationCode: string;
  totpCode: string;
}

interface SignInFormProps {
  onGoogleSignIn: () => Promise<void>;
  onFacebookSignIn: () => Promise<void>;
  onPasskeySignIn: () => Promise<void>;
  onSendSmsCode: (phoneNumber: string) => Promise<void>;
  onVerifySmsCode: (code: string) => Promise<void>;
  onVerifyTOTP: (email: string, code: string) => Promise<void>;
  error?: string;
}

export const SignInForm: React.FC<SignInFormProps> = ({
  onGoogleSignIn,
  onFacebookSignIn,
  onPasskeySignIn,
  onSendSmsCode,
  onVerifySmsCode,
  onVerifyTOTP,
  error,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [smsCodeSent, setSmsCodeSent] = useState(false);
  const [authState, setAuthState] = useState<AuthState>({
    email: '',
    phoneNumber: '',
    verificationCode: '',
    totpCode: '',
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setSmsCodeSent(false);
    setAuthState({
      email: '',
      phoneNumber: '',
      verificationCode: '',
      totpCode: '',
    });
  };

  const handleSendSmsCode = async () => {
    try {
      await onSendSmsCode(authState.phoneNumber);
      setSmsCodeSent(true);
    } catch (error) {
      console.error('Error sending SMS code:', error);
    }
  };

  const handleVerifySmsCode = async () => {
    try {
      await onVerifySmsCode(authState.verificationCode);
    } catch (error) {
      console.error('Error verifying SMS code:', error);
    }
  };

  const handleVerifyTOTP = async () => {
    try {
      await onVerifyTOTP(authState.email, authState.totpCode);
    } catch (error) {
      console.error('Error verifying TOTP:', error);
    }
  };

  const neonGreen = '#39FF14';

  return (
    <Box sx={{ 
      width: '100%', 
      maxWidth: { xs: '100%', sm: 500 },
      minWidth: { xs: '100%', sm: 400 },
      px: { xs: 2, sm: 0 }
    }}>
      <Paper 
        elevation={3}
        sx={{
          p: { xs: 2, sm: 4 },
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(10px)',
          border: `1px solid ${neonGreen}44`,
          width: '100%',
        }}
      >
        <Typography 
          variant="h4" 
          component="h1" 
          gutterBottom 
          align="center" 
          sx={{ 
            color: neonGreen,
            fontFamily: 'Orbitron, sans-serif',
            fontSize: { xs: '2rem', sm: '2.5rem' },
            fontWeight: 600,
            textShadow: `0 0 10px ${neonGreen}88`,
            letterSpacing: '0.05em',
            textTransform: 'uppercase'
          }}
        >
          Sign In
        </Typography>

        <Box sx={{ 
          borderBottom: 1, 
          borderColor: 'divider', 
          mb: 3, 
          width: '100%',
          display: 'flex',
          justifyContent: 'center'
        }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              '& .MuiTab-root': {
                minWidth: { xs: 'auto', sm: 100 },
                color: 'grey.500',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                padding: { xs: '6px 8px', sm: '6px 16px' },
                '& .MuiSvgIcon-root': {
                  fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  mb: { xs: 0.5, sm: 1 },
                },
              }
            }}
          >
            <Tab
              icon={<KeyIcon />}
              label="Passkey"
              sx={{
                '&.Mui-selected': {
                  color: neonGreen,
                },
              }}
            />
            <Tab
              icon={<QrCodeIcon />}
              label="Authenticator App"
              sx={{
                '&.Mui-selected': {
                  color: neonGreen,
                },
              }}
            />
            <Tab
              icon={<PhoneIcon />}
              label="SMS"
              sx={{
                '&.Mui-selected': {
                  color: neonGreen,
                },
              }}
            />
          </Tabs>
        </Box>

        <Box sx={{ 
          mt: 2, 
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
          '& > *': { // This targets immediate children
            maxWidth: '300px',
            width: '100%',
          }
        }}>
          {activeTab === 0 && (
            <Button
              variant="contained"
              fullWidth
              startIcon={<KeyIcon />}
              onClick={onPasskeySignIn}
              sx={{
                backgroundColor: neonGreen,
                color: 'black',
                '&:hover': {
                  backgroundColor: `${neonGreen}dd`,
                },
              }}
            >
              Sign in with Passkey
            </Button>
          )}

          {activeTab === 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Email"
                type="email"
                value={authState.email}
                onChange={(e) => setAuthState(prev => ({ ...prev, email: e.target.value }))}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: neonGreen }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: `${neonGreen}44`,
                    },
                    '&:hover fieldset': {
                      borderColor: `${neonGreen}88`,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: neonGreen,
                    },
                  },
                }}
              />

              <TextField
                label="TOTP Code"
                value={authState.totpCode}
                onChange={(e) => setAuthState(prev => ({ ...prev, totpCode: e.target.value }))}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: neonGreen }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: `${neonGreen}44`,
                    },
                    '&:hover fieldset': {
                      borderColor: `${neonGreen}88`,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: neonGreen,
                    },
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={handleVerifyTOTP}
                disabled={!authState.totpCode || !authState.email}
                sx={{
                  backgroundColor: neonGreen,
                  color: 'black',
                  '&:hover': {
                    backgroundColor: `${neonGreen}dd`,
                  },
                }}
              >
                Verify TOTP
              </Button>
            </Box>
          )}

          {activeTab === 2 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Phone Number"
                value={authState.phoneNumber}
                onChange={(e) => setAuthState(prev => ({ ...prev, phoneNumber: e.target.value }))}
                disabled={smsCodeSent}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon sx={{ color: neonGreen }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: `${neonGreen}44`,
                    },
                    '&:hover fieldset': {
                      borderColor: `${neonGreen}88`,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: neonGreen,
                    },
                  },
                }}
              />
              
              {!smsCodeSent ? (
                <Button
                  variant="contained"
                  onClick={handleSendSmsCode}
                  disabled={!authState.phoneNumber}
                  sx={{
                    backgroundColor: neonGreen,
                    color: 'black',
                    '&:hover': {
                      backgroundColor: `${neonGreen}dd`,
                    },
                  }}
                >
                  Send Code
                </Button>
              ) : (
                <>
                  <TextField
                    label="Verification Code"
                    value={authState.verificationCode}
                    onChange={(e) => setAuthState(prev => ({ ...prev, verificationCode: e.target.value }))}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: `${neonGreen}44`,
                        },
                        '&:hover fieldset': {
                          borderColor: `${neonGreen}88`,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: neonGreen,
                        },
                      },
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleVerifySmsCode}
                    disabled={!authState.verificationCode}
                    sx={{
                      backgroundColor: neonGreen,
                      color: 'black',
                      '&:hover': {
                        backgroundColor: `${neonGreen}dd`,
                      },
                    }}
                  >
                    Verify Code
                  </Button>
                </>
              )}
            </Box>
          )}
        </Box>

        <Box sx={{ mt: 4, width: '100%' }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            my: 2
          }}>
            <Box sx={{ flex: 1, height: '1px', bgcolor: `${neonGreen}44` }} />
            <Typography 
              variant="body2" 
              sx={{ 
                px: 2, 
                color: 'grey.500',
                fontSize: '0.875rem'
              }}
            >
              or continue with
            </Typography>
            <Box sx={{ flex: 1, height: '1px', bgcolor: `${neonGreen}44` }} />
          </Box>

          <Box sx={{ 
            display: 'flex', 
            gap: 2,
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <Button
              variant="outlined"
              onClick={onGoogleSignIn}
              startIcon={<GoogleIcon />}
              sx={{
                borderColor: `${neonGreen}44`,
                color: 'white',
                width: '140px',
                '&:hover': {
                  borderColor: neonGreen,
                  backgroundColor: `${neonGreen}11`,
                },
              }}
            >
              Google
            </Button>

            <Button
              variant="outlined"
              onClick={onFacebookSignIn}
              startIcon={<FacebookIcon />}
              sx={{
                borderColor: `${neonGreen}44`,
                color: 'white',
                width: '140px',
                '&:hover': {
                  borderColor: neonGreen,
                  backgroundColor: `${neonGreen}11`,
                },
              }}
            >
              Facebook
            </Button>
          </Box>
        </Box>

        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
            {error}
          </Typography>
        )}

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'grey.500' }}>
            Don't have an account?{' '}
            <Link
              component={RouterLink}
              to="/sign-up"
              sx={{
                color: neonGreen,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Sign up
            </Link>
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};